import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, MapPin, ChevronDown } from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';
import { useKitchenAuth } from '../context/KitchenAuthContext';

const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'https://biryani.com/api';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming','District of Columbia',
];
const STORE_TYPES = [
  { value: 'restaurant',    icon: '🍽️', label: 'Restaurant',    desc: 'Dine-in & takeaway' },
  { value: 'cloud_kitchen', icon: '☁️', label: 'Cloud Kitchen', desc: 'Delivery only' },
  { value: 'home_kitchen',  icon: '🏠', label: 'Home Kitchen',  desc: 'Home-based food' },
];
const FOOD_TYPES = [
  { value: 'veg',     label: '🥦 Veg Only' },
  { value: 'non_veg', label: '🍗 Non-Veg Only' },
  { value: 'both',    label: '🌿🍗 Veg & Non-Veg' },
];

function SectionHeader({ step, title, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--k-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
        {step}
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--k-text)' }}>{title}</div>
        {sub && <div style={{ fontSize: 13, color: 'var(--k-text-sub)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function FGroup({ label, required, children, hint, error }) {
  return (
    <div className="k-form-group">
      {label && <label className={`k-label${required ? ' required' : ''}`}>{label}</label>}
      {children}
      {hint && <div style={{ fontSize: 12, color: 'var(--k-text-sub)', marginTop: 4 }}>{hint}</div>}
      {error && <div className="k-error-text">{error}</div>}
    </div>
  );
}

export default function RegisterPage() {
  const { login }    = useKitchenAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { verification_token, phone: initPhone, countryCode: initCC } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});
  const [submitError, setSubmitError] = useState('');
  const [showStateDrop, setShowStateDrop] = useState(false);
  const [stateSearch, setStateSearch]     = useState('');

  // Location autocomplete
  const [locQuery,   setLocQuery]   = useState('');
  const [locResults, setLocResults] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const debRef = useRef(null);

  // ── Fields ──────────────────────────────────────────────────────
  const [form, setForm] = useState({
    firstName: '', middleName: '', lastName: '', businessName: '',
    phone: initPhone ? `${initCC || '+1'} ${initPhone}` : '',
    street: '', city: '', state: '', zipCode: '',
    storeType: '', foodType: '',
    contactName: '', contactPhone: '',
    agreeTerms: false, agreeCommission: false, agreeLegal: false,
  });
  const [logoFile,  setLogoFile]  = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const logoRef  = useRef(null);
  const coverRef = useRef(null);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setE = (k, v) => setErrors(e => ({ ...e, [k]: v }));

  // Location search
  useEffect(() => {
    if (!locQuery.trim() || locQuery.length < 3) { setLocResults([]); return; }
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLocLoading(true);
      try {
        const token = kitchenApi.getToken();
        const r = await fetch(`${API_BASE}/places/autocomplete?input=${encodeURIComponent(locQuery)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const d = await r.json();
        setLocResults(d?.data?.predictions || d?.predictions || []);
      } catch { setLocResults([]); }
      finally { setLocLoading(false); }
    }, 400);
  }, [locQuery]);

  const selectPlace = async (placeId, desc) => {
    setLocQuery(desc);
    setLocResults([]);
    try {
      const token = kitchenApi.getToken();
      const r = await fetch(`${API_BASE}/places/details?place_id=${placeId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const d = await r.json();
      const c = d?.data?.result?.address_components || d?.result?.address_components || [];
      const get = (types) => c.find(x => types.some(t => x.types.includes(t)));
      const streetNum = get(['street_number'])?.long_name || '';
      const route     = get(['route'])?.long_name || '';
      const city      = get(['locality'])?.long_name || get(['sublocality'])?.long_name || '';
      const state     = get(['administrative_area_level_1'])?.short_name || '';
      const zip       = get(['postal_code'])?.long_name || '';
      setF('street',  [streetNum, route].filter(Boolean).join(' '));
      setF('city',    city);
      setF('state',   state);
      setF('zipCode', zip);
    } catch (e) { console.error(e); }
  };

  // ── Validation ──────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.businessName.trim()) e.businessName = 'Required';
    if (!form.street.trim())    e.street    = 'Required';
    if (!form.city.trim())      e.city      = 'Required';
    if (!form.state.trim())     e.state     = 'Required';
    if (!form.zipCode.trim())   e.zipCode   = 'Required';
    if (!form.storeType)        e.storeType = 'Please select a kitchen type';
    if (!form.foodType)         e.foodType  = 'Please select food type';
    if (!form.agreeTerms)       e.agreeTerms = 'You must agree to the terms';
    return e;
  };

  // ── Submit ──────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setSubmitError('');
    try {
      const fd = new FormData();
      if (verification_token) fd.append('verification_token', verification_token);
      fd.append('first_name',        form.firstName.trim());
      fd.append('middle_name',       form.middleName.trim());
      fd.append('last_name',         form.lastName.trim());
      fd.append('business_name',     form.businessName.trim());  // Changed from kitchen_name
      fd.append('phone',             form.phone.trim());
      fd.append('street',            form.street.trim());  // Changed from address_line1
      fd.append('city',              form.city.trim());
      fd.append('state',             form.state.trim());
      fd.append('zip_code',          form.zipCode.trim());  // Changed from postal_code
      fd.append('country',           'US');
      fd.append('store_type',        form.storeType);
      fd.append('food_type',         form.foodType);
      fd.append('contact_person_name', form.contactName.trim());
      fd.append('contact_person_phone', form.contactPhone.trim());
      if (logoFile)  fd.append('logo',       logoFile);
      if (coverFile) fd.append('cover_image', coverFile);

      const res = await kitchenApi.completeKitchenRegistration(fd);
      if (res.success) {
        const d = res.data?.data || res.data;
        login(d?.user || d);
        navigate('/kitchen/dashboard', { replace: true });
      } else {
        setSubmitError(res.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredStates = US_STATES.filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()));

  return (
    <div className="k-auth-wrap" style={{ background: 'linear-gradient(135deg, #FFF7F3 0%, #F8FAFC 60%, #F0F4FF 100%)', alignItems: 'flex-start', padding: '32px 24px' }}>
      <div style={{ width: '100%', maxWidth: 640, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Link to="/kitchen/login" style={{ textDecoration: 'none' }}>
            <button className="k-btn-icon"><ArrowLeft size={18} /></button>
          </Link>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--k-text)', margin: 0 }}>Register Your Kitchen</h1>
            <p style={{ fontSize: 13, color: 'var(--k-text-sub)', margin: 0 }}>Join the Biryani kitchen network</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* ── 1. Your Details ── */}
          <div className="k-card" style={{ marginBottom: 20 }}>
            <SectionHeader step="1" title="Your Details" sub="Tell us about yourself" />
            <div className="k-grid-2">
              <FGroup label="First Name" required error={errors.firstName}>
                <input className={`k-input${errors.firstName ? ' k-input-error' : ''}`} placeholder="First name" value={form.firstName} onChange={e => { setF('firstName', e.target.value); setE('firstName', ''); }} />
              </FGroup>
              <FGroup label="Last Name" required error={errors.lastName}>
                <input className={`k-input${errors.lastName ? ' k-input-error' : ''}`} placeholder="Last name" value={form.lastName} onChange={e => { setF('lastName', e.target.value); setE('lastName', ''); }} />
              </FGroup>
            </div>
            <FGroup label="Middle Name">
              <input className="k-input" placeholder="Middle name (optional)" value={form.middleName} onChange={e => setF('middleName', e.target.value)} />
            </FGroup>
            <FGroup label="Kitchen / Business Name" required error={errors.businessName}>
              <input className={`k-input${errors.businessName ? ' k-input-error' : ''}`} placeholder="e.g. Mama's Kitchen" value={form.businessName} onChange={e => { setF('businessName', e.target.value); setE('businessName', ''); }} />
            </FGroup>
            <FGroup label="Phone">
              <input className="k-input" placeholder="+1 555-000-0000" value={form.phone} onChange={e => setF('phone', e.target.value)} />
            </FGroup>
          </div>

          {/* ── 2. Address / Location ── */}
          <div className="k-card" style={{ marginBottom: 20 }}>
            <SectionHeader step="2" title="Kitchen Location" sub="Your kitchen's delivery address" />

            {/* Address autocomplete */}
            <FGroup label="Search Address">
              <div style={{ position: 'relative' }}>
                <div className="k-input-group">
                  <span className="k-input-icon"><MapPin size={16} /></span>
                  <input
                    className="k-input"
                    placeholder="Start typing your address..."
                    value={locQuery}
                    onChange={e => setLocQuery(e.target.value)}
                  />
                </div>
                {locLoading && <div style={{ fontSize: 12, color: 'var(--k-text-sub)', marginTop: 4 }}>Searching...</div>}
                {locResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--k-border)', borderRadius: 8, boxShadow: 'var(--k-shadow-lg)', zIndex: 100, maxHeight: 220, overflowY: 'auto' }}>
                    {locResults.map((r, i) => (
                      <button
                        key={i}
                        type="button"
                        className="k-dropdown-item"
                        onClick={() => selectPlace(r.place_id, r.description)}
                      >
                        <MapPin size={14} style={{ flexShrink: 0 }} /> {r.description}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </FGroup>

            <FGroup label="Street Address" required error={errors.street}>
              <input className={`k-input${errors.street ? ' k-input-error' : ''}`} placeholder="123 Main St" value={form.street} onChange={e => { setF('street', e.target.value); setE('street', ''); }} />
            </FGroup>
            <div className="k-grid-2">
              <FGroup label="City" required error={errors.city}>
                <input className={`k-input${errors.city ? ' k-input-error' : ''}`} placeholder="City" value={form.city} onChange={e => { setF('city', e.target.value); setE('city', ''); }} />
              </FGroup>
              <FGroup label="Zip Code" required error={errors.zipCode}>
                <input className={`k-input${errors.zipCode ? ' k-input-error' : ''}`} placeholder="12345" value={form.zipCode} onChange={e => { setF('zipCode', e.target.value); setE('zipCode', ''); }} />
              </FGroup>
            </div>
            <FGroup label="State" required error={errors.state}>
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  className={`k-input${errors.state ? ' k-input-error' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => setShowStateDrop(p => !p)}
                >
                  {form.state || <span style={{ color: 'var(--k-text-muted)' }}>Select state</span>}
                  <ChevronDown size={16} />
                </button>
                {showStateDrop && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--k-border)', borderRadius: 8, boxShadow: 'var(--k-shadow-lg)', zIndex: 100 }}>
                    <div style={{ padding: 8 }}>
                      <input className="k-input" placeholder="Search states..." value={stateSearch} onChange={e => setStateSearch(e.target.value)} autoFocus />
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {filteredStates.map(s => (
                        <button key={s} type="button" className="k-dropdown-item" onClick={() => { setF('state', s); setShowStateDrop(false); setStateSearch(''); setE('state', ''); }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </FGroup>
          </div>

          {/* ── 3. Kitchen Info ── */}
          <div className="k-card" style={{ marginBottom: 20 }}>
            <SectionHeader step="3" title="Kitchen Info" sub="Help customers understand your kitchen" />

            <FGroup label="Kitchen Type" required error={errors.storeType}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {STORE_TYPES.map(st => (
                  <button
                    key={st.value}
                    type="button"
                    style={{
                      border: `2px solid ${form.storeType === st.value ? 'var(--k-primary)' : 'var(--k-border)'}`,
                      background: form.storeType === st.value ? 'var(--k-primary-bg)' : 'var(--k-surface)',
                      borderRadius: 10,
                      padding: '14px 10px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'border-color .15s, background .15s',
                    }}
                    onClick={() => { setF('storeType', st.value); setE('storeType', ''); }}
                  >
                    <div style={{ fontSize: 22 }}>{st.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6, color: 'var(--k-text)' }}>{st.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--k-text-sub)', marginTop: 2 }}>{st.desc}</div>
                  </button>
                ))}
              </div>
            </FGroup>

            <FGroup label="Food Type" required error={errors.foodType}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {FOOD_TYPES.map(ft => (
                  <button
                    key={ft.value}
                    type="button"
                    style={{
                      border: `2px solid ${form.foodType === ft.value ? 'var(--k-primary)' : 'var(--k-border)'}`,
                      background: form.foodType === ft.value ? 'var(--k-primary-bg)' : 'var(--k-surface)',
                      borderRadius: 8, padding: '10px 16px',
                      cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--k-text)',
                      transition: 'border-color .15s, background .15s',
                    }}
                    onClick={() => { setF('foodType', ft.value); setE('foodType', ''); }}
                  >
                    {ft.label}
                  </button>
                ))}
              </div>
            </FGroup>

            <div className="k-grid-2">
              <FGroup label="Contact Person Name">
                <input className="k-input" placeholder="Name" value={form.contactName} onChange={e => setF('contactName', e.target.value)} />
              </FGroup>
              <FGroup label="Contact Phone">
                <input className="k-input" placeholder="+1 555-000-0000" value={form.contactPhone} onChange={e => setF('contactPhone', e.target.value)} />
              </FGroup>
            </div>
          </div>

          {/* ── 4. Images ── */}
          <div className="k-card" style={{ marginBottom: 20 }}>
            <SectionHeader step="4" title="Kitchen Images" sub="Optional but recommended" />
            <div className="k-grid-2">
              {[{ key: 'logo', label: 'Logo', ref: logoRef, file: logoFile, set: setLogoFile },
                { key: 'cover', label: 'Cover Image', ref: coverRef, file: coverFile, set: setCoverFile }]
              .map(({ key, label, ref, file, set }) => (
                <div key={key}>
                  <label className="k-label">{label}</label>
                  <div
                    className="k-img-upload"
                    onClick={() => ref.current?.click()}
                  >
                    {file ? (
                      <div>
                        <img src={URL.createObjectURL(file)} alt={label} className="k-img-preview" />
                        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--k-text-sub)' }}>{file.name}</div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>📸</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--k-text-sub)' }}>Click to upload</div>
                        <div style={{ fontSize: 12, color: 'var(--k-text-muted)', marginTop: 4 }}>JPG, PNG up to 5MB</div>
                      </div>
                    )}
                    <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => set(e.target.files[0] || null)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 5. Agreements ── */}
          <div className="k-card" style={{ marginBottom: 24 }}>
            <SectionHeader step="5" title="Agreements" sub="Please read and agree to the following" />
            {[
              { key: 'agreeTerms',      label: 'I agree to the Terms & Conditions and Privacy Policy' },
              { key: 'agreeCommission', label: 'I understand and agree to the commission structure' },
              { key: 'agreeLegal',      label: 'I confirm all information provided is accurate and legal' },
            ].map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={e => setF(key, e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: 'var(--k-primary)', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, color: 'var(--k-text)', lineHeight: 1.5 }}>{label}</span>
              </label>
            ))}
            {errors.agreeTerms && <div className="k-error-text">{errors.agreeTerms}</div>}
          </div>

          {submitError && (
            <div style={{ background: 'var(--k-danger-bg)', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', color: '#DC2626', fontSize: 14, marginBottom: 16 }}>
              {submitError}
            </div>
          )}

          <button
            type="submit"
            className="k-btn k-btn-primary k-btn-lg"
            style={{ width: '100%' }}
            disabled={loading || !form.agreeTerms}
          >
            {loading
              ? <><span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> Registering your kitchen...</>
              : <><CheckCircle size={18} /> Complete Registration</>
            }
          </button>

          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--k-text-sub)', marginTop: 16 }}>
            Already have an account? <Link to="/kitchen/login" style={{ color: 'var(--k-primary)', fontWeight: 600 }}>Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
